import api from '@/services/api' // Instancia global de Axios con configuración

const handleApiError = (commit, error) => {
  const serverError = error.response?.data || {
    message: 'Error inesperado',
    code_number: 500
  }
  commit('SET_ERROR', serverError)
  commit('SET_FIELD_ERRORS', error.response?.data?.errors || {})
  return serverError // No usar throw, sino devolver el error
}

export default {
  namespaced: true,
  state: {
    items: [],
    currentPage: 1,
    lastPage: 1,
    totalItems: 0,
    itemsPerPage: 6,
    error: null,
    fieldErrors: {},
    successMessage: null,
    errorCode: null
  },
  getters: {
    items: state => state.items || [],
    fieldErrors: state => state.fieldErrors,
    error: state => state.error,
    errorCode: state => state.errorCode,
    successMessage: state => state.successMessage,
    totalItems: state => state.totalItems,
    currentPage: state => state.currentPage,
    lastPage: state => state.lastPage
  },
  mutations: {
    SET_ERROR(state, error) {
      state.error = error.message || null
      state.errorCode = error.code_number || null
    },
    CLEAR_FIELD_ERRORS(state) {
      state.fieldErrors = {}
    },
    CLEAR_ERROR(state) {
      state.error = null
      state.errorCode = null
      state.fieldErrors = {}
    },
    SET_FIELD_ERRORS(state, errors) {
      state.fieldErrors = errors
    },
    SET_SUCCESS_MESSAGE(state, message) {
      state.successMessage = message
    },
    CLEAR_SUCCESS_MESSAGE(state) {
      state.successMessage = null
    },
    SET_ITEMS(state, items) {
      state.items = Array.isArray(items) ? items : []
    },
    ADD_ITEM(state, item) {
      state.items.unshift(item)
    },
    UPDATE_ITEM(state, item) {
      const index = state.items.findIndex(p => p.id === item.id)
      if (index !== -1) {
        state.items.splice(index, 1, item)
      }
    },
    DELETE_ITEM(state, itemId) {
      state.items = state.items.filter(item => item.id !== itemId)
    },
    SET_PAGINATION(state, pagination) {
      state.currentPage = pagination.current_page
      state.lastPage = pagination.last_page
      state.totalItems = pagination.total
      state.itemsPerPage = pagination.per_page
    },
    SET_CURRENT_PAGE(state, currentPage) {
      state.currentPage = currentPage
    }
  },
  actions: {
    async fetchRecords({ commit, state }, { resource, options = {}, additionalParams = {} }) {
      const {
        page = state.currentPage,
        search = '',
        itemsPerPage = state.itemsPerPage,
        sortColumn,
        sortOrder,
        onlyDeleted = false
      } = options
      try {
        commit('CLEAR_ERROR')
        const response = await api.get(`/${resource}`, {
          params: {
            page,
            search,
            per_page: itemsPerPage,
            sort_column: sortColumn,
            sort_order: sortOrder,
            only_deleted: onlyDeleted,
            ...additionalParams
          }
        })
        const { data, pagination } = response.data
        commit('SET_ITEMS', data)
        commit('SET_PAGINATION', pagination)
        commit('SET_CURRENT_PAGE', page)
        return response
      } catch (error) {
        handleApiError(commit, error)
        throw error // Propaga el error
      }
    },

    async reloadRecords({ dispatch, state }, resource) {
      const { currentPage, itemsPerPage } = state
      await dispatch('fetchRecords', {
        resource,
        options: {
          page: currentPage,
          itemsPerPage,
          sortColumn: 'updated_at',
          sortOrder: 'desc'
        }
      })
    },

    async addModel({ commit, dispatch }, { resource, itemData }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.post(`/${resource}`, itemData)
        const { data, message } = response.data
        commit('ADD_ITEM', data)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return response
      } catch (error) {
        if (error.response && error.response.status === 422) {
          commit('SET_FIELD_ERRORS', error.response.data.errors || {})
        } else {
          handleApiError(commit, error)
        }
        throw error // Propaga el error
      }
    },

    async editModel({ commit, dispatch }, { resource, itemData }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.put(`/${resource}/${itemData.id}`, itemData)
        const { data, message } = response.data
        commit('UPDATE_ITEM', data)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return response
      } catch (error) {
        if (error.response && error.response.status === 422) {
          commit('SET_FIELD_ERRORS', error.response.data.errors || {})
        } else {
          handleApiError(commit, error)
        }
        throw error // Propaga el error
      }
    },

    async deleteRecord({ commit, dispatch }, { resource, itemId }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.delete(`/${resource}/${itemId}`)
        const { message } = response.data
        commit('DELETE_ITEM', itemId)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return response
      } catch (error) {
        if (error.response && error.response.status === 403) {
          const errorMessage =
            error.response.data.message ||
            'No tienes permiso para realizar esta acción.'
          commit('SET_ERROR', { message: errorMessage, code_number: 403 })
        } else {
          handleApiError(commit, error)
        }
        throw error // Propaga el error
      }
    },

    async restoreRecord({ commit, dispatch }, { resource, itemId }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.post(`/${resource}/${itemId}/restore`)
        const { data, message } = response.data
        commit('UPDATE_ITEM', data)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return response
      } catch (error) {
        handleApiError(commit, error)
      }
    }
  }
}
